import { motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import GlobalHeader from "@/components/GlobalHeader";
import Footer from "@/components/Footer";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";
import { saveEmail, savePhoneNumber, saveGeneralInquiry, saveSponsor, n8nSaveEmail, n8nSavePhone, n8nSaveInquiry, n8nSaveSponsor, n8nSaveEmailSS, n8nSavePhoneSS, n8nSaveInquirySS, n8nSaveSponsorSS, n8nSavePhoneSlickText } from "@/lib/firebase";
import { generalInquirySchema, sponsorSchema } from "@/lib/validation";
import { canSubmit, recordSubmission, getSecondsRemaining } from "@/lib/rate-limit";
import { PHOTOS } from "@/lib/photos";
import { useMobileOnceInView, useMobileOnceViewport } from "@/hooks/use-mobile-once-in-view";
import { usePageSEO } from "@/hooks/use-page-seo";

const images = {
  hero1: PHOTOS.GROUP.fourFriendsDrinks,
  hero2: PHOTOS.TWO_GUYS.hawaiianShirtsSmiling,
};

const Contact = () => {
  usePageSEO({
    title: "Contact Us — Yacht Rock Detroit 2026",
    description: "Get in touch with the Yacht Rock Detroit team. General inquiries and sponsorship opportunities.",
    keywords: "contact yacht rock detroit, sponsorship detroit festival",
    ogImage: PHOTOS.GROUP.fourFriendsDrinks,
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "Contact", url: "https://yachtrockdetroit.com/contact" },
    ],
  });

  const headerRef = useRef<HTMLElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });
  const mobileOnceViewport = useMobileOnceViewport("-100px");

  // Form states - General Inquiries
  const [generalForm, setGeneralForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [generalLoading, setGeneralLoading] = useState(false);

  // Form states - Sponsorship
  const [sponsorForm, setSponsorForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [sponsorLoading, setSponsorLoading] = useState(false);
  const [generalSuccess, setGeneralSuccess] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [sponsorSuccess, setSponsorSuccess] = useState(false);
  const [sponsorError, setSponsorError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  const handleGeneralSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = generalInquirySchema.safeParse(generalForm);
    if (!result.success) {
      setGeneralError(result.error.errors[0].message);
      return;
    }
    if (!canSubmit("general")) {
      setGeneralError(`Please wait ${getSecondsRemaining("general")}s before submitting again.`);
      return;
    }
    
    setGeneralLoading(true);
    setGeneralSuccess(false);
    setGeneralError(null);
    
    try {
      // Save to all relevant collections in parallel
      const promises = [
        saveEmail(generalForm.email, "general"),
        saveGeneralInquiry(generalForm),
        n8nSaveEmail(generalForm.email, "general").catch(console.error),
        n8nSaveInquiry(generalForm).catch(console.error),
        n8nSaveEmailSS(generalForm.email, "general").catch(console.error),
        n8nSaveInquirySS(generalForm).catch(console.error),
      ];
      
      // Only save phone if provided
      if (generalForm.phone.trim()) {
        promises.push(savePhoneNumber(generalForm.phone, "general"));
        promises.push(n8nSavePhone(generalForm.phone, "general").catch(console.error));
        promises.push(n8nSavePhoneSS(generalForm.phone, "general").catch(console.error));
        promises.push(n8nSavePhoneSlickText(generalForm.phone).catch(console.error));
      }
      
      await Promise.all(promises);
      
      setGeneralSuccess(true);
      recordSubmission("general");
      setGeneralForm({ firstName: "", lastName: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setGeneralLoading(false);
    }
  };

  const handleSponsorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = sponsorSchema.safeParse(sponsorForm);
    if (!result.success) {
      setSponsorError(result.error.errors[0].message);
      return;
    }
    if (!canSubmit("sponsor")) {
      setSponsorError(`Please wait ${getSecondsRemaining("sponsor")}s before submitting again.`);
      return;
    }
    
    setSponsorLoading(true);
    setSponsorSuccess(false);
    setSponsorError(null);
    
    try {
      // Save to all relevant collections in parallel
      const promises = [
        saveEmail(sponsorForm.email, "sponsor"),
        saveSponsor(sponsorForm),
        n8nSaveEmail(sponsorForm.email, "sponsor").catch(console.error),
        n8nSaveSponsor(sponsorForm).catch(console.error),
        n8nSaveEmailSS(sponsorForm.email, "sponsor").catch(console.error),
        n8nSaveSponsorSS(sponsorForm).catch(console.error),
      ];
      
      // Only save phone if provided
      if (sponsorForm.phone.trim()) {
        promises.push(savePhoneNumber(sponsorForm.phone, "sponsor"));
        promises.push(n8nSavePhone(sponsorForm.phone, "sponsor").catch(console.error));
        promises.push(n8nSavePhoneSS(sponsorForm.phone, "sponsor").catch(console.error));
        promises.push(n8nSavePhoneSlickText(sponsorForm.phone).catch(console.error));
      }
      
      await Promise.all(promises);
      
      setSponsorSuccess(true);
      recordSubmission("sponsor");
      setSponsorForm({ firstName: "", lastName: "", company: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setSponsorLoading(false);
    }
  };

  const inputClasses = "w-full px-4 py-3 bg-white border border-yacht-charcoal/20 rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/40 focus:outline-none focus:border-yacht-seaglass focus:ring-2 focus:ring-yacht-seaglass/20 transition-all";
  const inputClassesDark = "w-full px-4 py-3 bg-yacht-cream border border-yacht-cream/30 rounded-xl text-yacht-charcoal placeholder:text-yacht-charcoal/50 focus:outline-none focus:border-yacht-yellow focus:ring-2 focus:ring-yacht-yellow/20 transition-all";
  const labelClasses = "block text-sm font-medium text-yacht-charcoal/70 mb-2";
  const labelClassesDark = "block text-sm font-medium text-yacht-cream mb-2";

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />

      {/* Hero Header Section */}
      <section 
        ref={headerRef}
        className="relative min-h-fit md:min-h-[80vh] bg-yacht-blue overflow-hidden flex flex-col md:flex-row md:items-end"
      >
        {/* Noise texture overlay */}
        <div 
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Boats graphic background */}
        <img
          src={boatsGraphic}
          alt=""
          className="absolute pointer-events-none z-0
            w-[800%] left-1/2 -translate-x-1/2 top-0
            sm:w-[150%] sm:-top-[30%]
            lg:w-[90%] lg:-left-[10%] lg:-top-[30%] lg:translate-x-0"
          style={{
            opacity: 0.15,
            filter: 'brightness(0) invert(1)',
          }}
        />

        {/* MOBILE LAYOUT */}
        <div className="md:hidden relative z-20 flex flex-col items-center pt-28 pb-8 px-6">
          <motion.p
            className="text-yacht-yellow/80 text-sm font-medium tracking-[0.3em] uppercase mb-3 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            Get In Touch
          </motion.p>
          <div className="text-[8vw] font-bold text-white leading-[0.9] tracking-tight text-center mb-8" role="heading" aria-level={1}>
            <GradualSpacing text="Contact" duration={0.5} delayMultiple={0.04} />
            <br />
            <GradualSpacing text="Yacht Rock Detroit" duration={0.5} delayMultiple={0.04} />
          </div>
          
          {/* Images overlapping - Mobile */}
          <div className="relative w-full max-w-xs h-[320px]">
            <motion.div
              className="absolute left-0 top-0 w-[60%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-10"
              initial={{ opacity: 0, y: 30, rotate: -5 }}
              animate={isHeaderInView ? { opacity: 1, y: 0, rotate: -5 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <img src={images.hero1} alt="Four friends enjoying drinks at The Roostertail" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div
              className="absolute right-0 top-8 w-[60%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-20"
              initial={{ opacity: 0, y: 30, rotate: 5 }}
              animate={isHeaderInView ? { opacity: 1, y: 0, rotate: 5 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <img src={images.hero2} alt="Two friends in Hawaiian shirts smiling at the festival" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>

        {/* DESKTOP LAYOUT - Photos on right */}
        <motion.div
          className="hidden md:block absolute top-44 right-[5%] w-[35%] max-w-md aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl"
          initial={{ opacity: 0, y: 50, rotate: 3 }}
          animate={isHeaderInView ? { opacity: 1, y: 0, rotate: 3 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <img src={images.hero1} alt="Four friends enjoying drinks at The Roostertail" className="w-full h-full object-cover" />
        </motion.div>

        <motion.div
          className="hidden md:block absolute bottom-24 right-[25%] w-[25%] max-w-xs aspect-square rounded-2xl overflow-hidden shadow-2xl z-10"
          initial={{ opacity: 0, y: 30, rotate: -5 }}
          animate={isHeaderInView ? { opacity: 1, y: 0, rotate: -5 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <img src={images.hero2} alt="Two friends in Hawaiian shirts smiling at the festival" className="w-full h-full object-cover" />
        </motion.div>

        {/* Big Title - Bottom Left (Desktop only) */}
        <div className="hidden md:block relative z-20 px-8 md:px-16 pb-16 md:pb-24">
          <motion.p
            className="text-yacht-yellow/80 text-sm md:text-base font-medium tracking-[0.3em] uppercase mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            Get In Touch
          </motion.p>
          <h1 className="text-[10vw] lg:text-[8vw] font-bold text-white leading-[0.85] tracking-tight">
            <GradualSpacing text="Contact" duration={0.5} delayMultiple={0.08} />
          </h1>
        </div>
      </section>

      {/* General Inquiries Section */}
      <section className="py-20 md:py-32 px-6 md:px-16 bg-yacht-cream overflow-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Big Title - Two lines, off-centered like Sponsor section */}
          <motion.h2 
            className="text-[12vw] md:text-[10vw] lg:text-[8vw] font-bold text-yacht-charcoal leading-[0.85] tracking-tight mb-2 md:mb-4 text-right"
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            GENERAL
          </motion.h2>
          <motion.h2 
            className="text-[12vw] md:text-[10vw] lg:text-[8vw] font-bold text-yacht-seaglass leading-[0.85] tracking-tight mb-12 md:mb-16 italic text-right mr-8 md:mr-16"
            initial={{ opacity: 0, x: 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            INQUIRIES
          </motion.h2>

          <div className="max-w-2xl">
            <motion.p 
              className="text-yacht-charcoal/60 text-lg md:text-xl mb-10" 
              style={{ fontFamily: 'Georgia, serif' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={mobileOnceViewport}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Have a question? We'd love to hear from you.
            </motion.p>

            <motion.form
              onSubmit={handleGeneralSubmit}
              className="space-y-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={mobileOnceViewport}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="general-firstName" className={labelClasses}>First Name</label>
                  <input
                    type="text"
                    id="general-firstName"
                    name="fname"
                    autoComplete="given-name"
                    required
                    className={inputClasses}
                    placeholder="John"
                    value={generalForm.firstName}
                    onChange={(e) => setGeneralForm({ ...generalForm, firstName: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="general-lastName" className={labelClasses}>Last Name</label>
                  <input
                    type="text"
                    id="general-lastName"
                    name="lname"
                    autoComplete="family-name"
                    required
                    className={inputClasses}
                    placeholder="Doe"
                    value={generalForm.lastName}
                    onChange={(e) => setGeneralForm({ ...generalForm, lastName: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="general-email" className={labelClasses}>Email</label>
                  <input
                    type="email"
                    id="general-email"
                    name="email"
                    autoComplete="email"
                    inputMode="email"
                    required
                    className={inputClasses}
                    placeholder="john@example.com"
                    value={generalForm.email}
                    onChange={(e) => setGeneralForm({ ...generalForm, email: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="general-phone" className={labelClasses}>Phone Number</label>
                  <input
                    type="tel"
                    id="general-phone"
                    name="phone"
                    autoComplete="tel"
                    inputMode="tel"
                    className={inputClasses}
                    placeholder="(555) 123-4567"
                    value={generalForm.phone}
                    onChange={(e) => setGeneralForm({ ...generalForm, phone: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className={labelClasses}>Message</label>
                <textarea
                  required
                  rows={5}
                  className={inputClasses + " resize-none"}
                  placeholder="How can we help you?"
                  value={generalForm.message}
                  onChange={(e) => setGeneralForm({ ...generalForm, message: e.target.value })}
                />
              </div>

              <button
                type="submit"
                disabled={generalLoading}
                className="w-full md:w-auto px-10 py-4 bg-yacht-seaglass text-white font-bold text-lg rounded-full hover:scale-105 transition-transform shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {generalLoading ? "Sending..." : "Send Message"}
              </button>
              
              {generalError && (
                <p className="mt-4 text-red-600 text-sm font-medium">{generalError}</p>
              )}
              {generalSuccess && (
                <p className="mt-4 text-green-600 font-medium">Submission successful!</p>
              )}
            </motion.form>
          </div>
        </div>
      </section>

      {/* Sponsorship Section - Different Color */}
      <section className="py-20 md:py-32 px-6 md:px-16 bg-yacht-blue relative overflow-hidden">
        {/* Noise texture overlay */}
        <div 
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto">
          {/* Big Title - Off-centered */}
          <motion.h2 
            className="text-[12vw] md:text-[10vw] lg:text-[8vw] font-bold text-white leading-[0.85] tracking-tight mb-2 md:mb-4"
            initial={{ opacity: 0, x: 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            BECOME
          </motion.h2>
          <motion.h2 
            className="text-[12vw] md:text-[10vw] lg:text-[8vw] font-bold text-yacht-yellow leading-[0.85] tracking-tight mb-12 md:mb-16 italic ml-4 md:ml-12"
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={mobileOnceViewport}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            A SPONSOR
          </motion.h2>

          <div className="max-w-2xl mx-auto md:mx-0 md:ml-auto">
            <motion.p 
              className="text-white/70 text-lg md:text-xl mb-10" 
              style={{ fontFamily: 'Georgia, serif' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={mobileOnceViewport}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Partner with Detroit's smoothest summer festival.
            </motion.p>

            <motion.form
              onSubmit={handleSponsorSubmit}
              className="space-y-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={mobileOnceViewport}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="sponsor-firstName" className={labelClassesDark}>First Name</label>
                  <input
                    type="text"
                    id="sponsor-firstName"
                    name="fname"
                    autoComplete="given-name"
                    required
                    className={inputClassesDark}
                    placeholder="John"
                    value={sponsorForm.firstName}
                    onChange={(e) => setSponsorForm({ ...sponsorForm, firstName: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="sponsor-lastName" className={labelClassesDark}>Last Name</label>
                  <input
                    type="text"
                    id="sponsor-lastName"
                    name="lname"
                    autoComplete="family-name"
                    required
                    className={inputClassesDark}
                    placeholder="Doe"
                    value={sponsorForm.lastName}
                    onChange={(e) => setSponsorForm({ ...sponsorForm, lastName: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="sponsor-company" className={labelClassesDark}>Company Name</label>
                <input
                  type="text"
                  id="sponsor-company"
                  name="organization"
                  autoComplete="organization"
                  required
                  className={inputClassesDark}
                  placeholder="Acme Inc."
                  value={sponsorForm.company}
                  onChange={(e) => setSponsorForm({ ...sponsorForm, company: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="sponsor-email" className={labelClassesDark}>Email</label>
                  <input
                    type="email"
                    id="sponsor-email"
                    name="email"
                    autoComplete="email"
                    inputMode="email"
                    required
                    className={inputClassesDark}
                    placeholder="john@company.com"
                    value={sponsorForm.email}
                    onChange={(e) => setSponsorForm({ ...sponsorForm, email: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="sponsor-phone" className={labelClassesDark}>Phone Number</label>
                  <input
                    type="tel"
                    id="sponsor-phone"
                    name="phone"
                    autoComplete="tel"
                    inputMode="tel"
                    className={inputClassesDark}
                    placeholder="(555) 123-4567"
                    value={sponsorForm.phone}
                    onChange={(e) => setSponsorForm({ ...sponsorForm, phone: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className={labelClassesDark}>Message</label>
                <textarea
                  required
                  rows={5}
                  className={inputClassesDark + " resize-none"}
                  placeholder="Tell us about your sponsorship interest..."
                  value={sponsorForm.message}
                  onChange={(e) => setSponsorForm({ ...sponsorForm, message: e.target.value })}
                />
              </div>

              <button
                type="submit"
                disabled={sponsorLoading}
                className="w-full md:w-auto px-10 py-4 bg-yacht-yellow text-yacht-charcoal font-bold text-lg rounded-full hover:scale-105 transition-transform shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {sponsorLoading ? "Submitting..." : "Submit Inquiry"}
              </button>
              
              {sponsorError && (
                <p className="mt-4 text-red-400 text-sm font-medium">{sponsorError}</p>
              )}
              {sponsorSuccess && (
                <p className="mt-4 text-green-400 font-medium">Submission successful!</p>
              )}
            </motion.form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Contact;
